import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Redirect, Route, Switch } from 'react-router'
import { toTitleCase } from 'utils/stringHelpers'
import BackLink from 'components/BackLink'
import ProductLessons from 'components/ProductLessons'
import CreateCourse from 'experienceComponents/CreateCourse'
import CreateSmallGroup from 'experienceComponents/CreateSmallGroup'

import { Wrapper, SiteSelectorK8WrapperDark } from './component.styles'
import SiteSelector from 'components/SiteSelector'

import { TAB_TITLE } from 'containers/Product/constants'

const LessonsComponent = props => {
  const {
    classes = [],
    contentfulCourses,
    courseLinks,
    lessonCompletionGoalValue,
    match,
    goToDashboard,
    onComponentClick,
    onClassClick,
    productName,
    productTabTitle,
    onCourseClick,
    // CreateCourse props
    activeDigitalLicenses,
    lms,
    pathName,
    userAccess,
    userContext,
    userProfile,
    createCourseInstance,
    createCourseInstanceV2,
    createCourseInstanceV2ProgramFlex,
    deletingCourseCreatedStatus,
    deleteCourseInstance,
    updateCourseInstance,
  } = props || {}

  // Local state to control direct modal invocation
  const [isCreateCourseOpen, setIsCreateCourseOpen] = useState(false)
  const [isCreateSmallGroupOpen, setIsCreateSmallGroupOpen] = useState(false)

  const openCreateCourse = useCallback(() => setIsCreateCourseOpen(true), [])
  const openCreateSmallGroup = useCallback(
    () => setIsCreateSmallGroupOpen(true),
    [],
  )

  // Reset toggles after they trigger a modal open so they can be reused
  useEffect(() => {
    if (isCreateCourseOpen) setIsCreateCourseOpen(false)
  }, [isCreateCourseOpen])
  useEffect(() => {
    if (isCreateSmallGroupOpen) setIsCreateSmallGroupOpen(false)
  }, [isCreateSmallGroupOpen])

  const baseUrl = match.url
  const basePath = match.path
  const productTitle = toTitleCase(`${productName?.replace('-', ' ')} Lessons`)
  const withVerb = `${basePath}/:verb(preview|teach)`
  const tabTitles = {
    page: productTabTitle,
    tab: TAB_TITLE,
  }
  document.title = `${tabTitles.page} | ${tabTitles.tab}`

  return (
    <Wrapper>
      <Switch>
        {/* Previewing or teaching a with nothing specified; go to just /lessons */}
        <Redirect exact from={`${withVerb}`} to={`${baseUrl}`} />
        {/* Show courses to preview and classes to teach (if there are any) */}
        <Route
          path={baseUrl}
          render={() => (
            <>
              <nav className="breadcrumbs">
                <BackLink onClick={goToDashboard}>Dashboard</BackLink>
                <SiteSelectorK8WrapperDark>
                  <SiteSelector />
                </SiteSelectorK8WrapperDark>
              </nav>

              <ProductLessons
                classes={classes}
                contentfulCourses={contentfulCourses}
                courseLinks={courseLinks}
                lessonCompletionGoalValue={lessonCompletionGoalValue}
                onClassClick={onClassClick}
                onComponentClick={onComponentClick}
                onCourseClick={onCourseClick}
                onCreateClass={openCreateCourse}
                onCreateSmallGroup={openCreateSmallGroup}
                program={productName}
                title={productTitle}
              />
              {/* Direct modal invocation for CreateCourse/CreateSmallGroup */}
              <CreateCourse
                activeDigitalLicenses={activeDigitalLicenses}
                autoOpen={isCreateCourseOpen}
                contentfulCourses={contentfulCourses}
                createCourseInstance={createCourseInstance}
                createCourseInstanceV2={createCourseInstanceV2}
                createCourseInstanceV2ProgramFlex={
                  createCourseInstanceV2ProgramFlex
                }
                deleteCourseInstance={deleteCourseInstance}
                deletingCourseCreatedStatus={deletingCourseCreatedStatus}
                emptyClasses={true}
                fetchInstructorData={() => {}}
                hideLauncher={true}
                lms={lms}
                pathName={pathName}
                updateCourseInstance={updateCourseInstance}
                userAccess={userAccess}
                userContext={userContext}
                userProfile={userProfile}
              />
              <CreateSmallGroup
                activeDigitalLicenses={activeDigitalLicenses}
                autoOpen={isCreateSmallGroupOpen}
                contentfulCourses={contentfulCourses}
                createCourseInstance={createCourseInstance}
                createCourseInstanceV2={createCourseInstanceV2}
                createCourseInstanceV2ProgramFlex={
                  createCourseInstanceV2ProgramFlex
                }
                deleteCourseInstance={deleteCourseInstance}
                deletingCourseCreatedStatus={deletingCourseCreatedStatus}
                emptyClasses={true}
                fetchInstructorData={() => {}}
                hideLauncher={true}
                lms={lms}
                pathName={pathName}
                updateCourseInstance={updateCourseInstance}
                userAccess={userAccess}
                userContext={userContext}
                userProfile={userProfile}
              />
            </>
          )}
        />
      </Switch>
    </Wrapper>
  )
}

LessonsComponent.propTypes = {
  activeDigitalLicenses: PropTypes.array,
  classes: PropTypes.arrayOf(
    PropTypes.shape({
      contentfulID: PropTypes.string,
      completeLessons: PropTypes.number,
      instance: PropTypes.string,
      scoId: PropTypes.number,
      totalLessons: PropTypes.number,
      title: PropTypes.string,
    }),
  ),
  contentfulCourses: PropTypes.arrayOf(
    PropTypes.shape({
      displayTitle: PropTypes.string,
    }),
  ).isRequired,
  courseLinks: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string,
      title: PropTypes.string,
    }),
  ).isRequired,
  createCourseInstance: PropTypes.func,
  createCourseInstanceV2: PropTypes.func,
  createCourseInstanceV2ProgramFlex: PropTypes.func,
  deleteCourseInstance: PropTypes.func,
  deletingCourseCreatedStatus: PropTypes.func,
  goToDashboard: PropTypes.func.isRequired,
  lms: PropTypes.object,
  match: PropTypes.shape({
    url: PropTypes.string,
    path: PropTypes.string,
    params: PropTypes.shape({
      productName: PropTypes.string,
    }),
  }).isRequired,
  onClassClick: PropTypes.func,
  onComponentClick: PropTypes.func,
  onCourseClick: PropTypes.func.isRequired,
  pathName: PropTypes.string,
  productName: PropTypes.string,
  productTabTitle: PropTypes.string,
  updateCourseInstance: PropTypes.func,
  userAccess: PropTypes.object,
  userProfile: PropTypes.object,
}

export default LessonsComponent
